import React, { useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';

import { WIDGET_FILTER_OPTIONS, WIDGET_FILTER_TYPES } from 'common/Widgets/constants';
import AggregationTypePicker from 'components/lib/AggregationTypePicker';
import SelectAdvancedFilter from 'components/lib/SelectAdvancedFilter';
import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import { AGGREGATION_TYPE, AGGREGATION_TYPE_LABELS } from 'components/View/constants';
import { FeeType } from 'types/fees';

import { CMASummaryFilters } from '../types';

const CONFIG = {
  [FeeType.CARE_FEE]: {
    placeholder: 'Care Level',
    optionsKey: WIDGET_FILTER_TYPES.CARE_LEVEL,
    valuesKey: 'careLevels' as const,
    statsKey: 'careLevelsStatsType' as const,
    dataTestId: 'care-level-filter',
  },
  [FeeType.MEDICATION_FEE]: {
    placeholder: 'Medication Level',
    optionsKey: WIDGET_FILTER_TYPES.MEDICATION_LEVEL,
    valuesKey: 'medicationLevels' as const,
    statsKey: 'medicationLevelsStatsType' as const,
    dataTestId: 'med-level-filter',
  },
};

interface Props {
  type: FeeType;
}

const CMASummaryFeeLevelFilter = ({ type }: Props) => {
  const { viewFilters, setViewFilters } = useCommunityViewContext();
  const filters = viewFilters as CMASummaryFilters | undefined;

  const { placeholder, optionsKey, valuesKey, statsKey, dataTestId } = CONFIG[type];

  const levelValues = filters?.[valuesKey] ?? [];
  const selectedAggregation = filters?.[statsKey] ?? AGGREGATION_TYPE.AVG;
  const [priceDisplay, setPriceDisplay] = useState<number>(selectedAggregation);

  useEffect(() => {
    setPriceDisplay(filters?.[statsKey] ?? AGGREGATION_TYPE.AVG);
  }, [filters?.[statsKey]]);

  const options = useMemo(
    () =>
      WIDGET_FILTER_OPTIONS[optionsKey].map((item) => ({
        label: item.name,
        value: item.id,
      })),
    [optionsKey]
  );

  const handleSelect = (values: any[]) => {
    setViewFilters((prevState: any) => ({
      ...prevState,
      [valuesKey]: values.map((item: any) => item.value),
      [statsKey]: priceDisplay,
    }));
  };

  return (
    <SelectAdvancedFilter
      className={classNames({ active: levelValues && levelValues.length > 0 })}
      placeholder={placeholder}
      options={options}
      defaultValue={levelValues}
      onSelect={handleSelect}
      otherOptions={
        <AggregationTypePicker aggregationType={priceDisplay} label="PRICE DISPLAY" onSelect={setPriceDisplay} />
      }
      otherOptionsLabel={AGGREGATION_TYPE_LABELS[selectedAggregation]}
      dataTestId={dataTestId}
    />
  );
};

export default CMASummaryFeeLevelFilter;
